// pages/student/StudentSetting.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  InputAdornment,
} from "@mui/material";
import {
  Home,
  Settings,
  Person,
  Email,
  Phone,
  Lock,
  Notifications,
  Security,
  Language,
  Save,
  Edit,
  Verified,
  ArrowForward,
  Visibility,
  VisibilityOff,
  School,
  DarkMode,
  LightMode,
  Delete,
} from "@mui/icons-material";
import SideBar from "./components/SideBar";
import { postData } from "../../services/Request";
import "../../styles/Course.css";

const StudentSetting = () => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [userInfo, setUserInfo] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  // Settings states
  const [settings, setSettings] = useState({
    notifications: true,
    emailUpdates: true,
    darkMode: false,
    twoFactorAuth: false,
    language: "English",
    privacy: "Public",
  });

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user || user === "null") {
      nav("/");
      return;
    }

    try {
      const data = JSON.parse(user);
      setUserInfo(data);
      setFormData({
        firstName: data.firstname || "",
        lastName: data.lastname || "",
        email: data.email || "",
        phone: data.phone || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error parsing user data:", error);
      nav("/");
    }
  }, [nav]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSettingChange = (setting) => {
    setSettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }
    if (
      formData.newPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const details = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
    };

    // Update profile API call
    setTimeout(() => {
      setLoading(false);
      setSnackbar({
        open: true,
        message: "Profile updated successfully!",
        severity: "success",
      });
      // Update localStorage
      if (userInfo) {
        const updatedUser = { ...userInfo, ...details };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserInfo(updatedUser);
      }
    }, 1000);
  };

  const handleChangePassword = () => {
    if (!formData.currentPassword) {
      setSnackbar({
        open: true,
        message: "Please enter your current password",
        severity: "error",
      });
      return;
    }
    if (!formData.newPassword || formData.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: "New password must be at least 6 characters",
        severity: "error",
      });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setSnackbar({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    // Change password API call
    setTimeout(() => {
      setLoading(false);
      setSnackbar({
        open: true,
        message: "Password changed successfully!",
        severity: "success",
      });
      setFormData((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    }, 1000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const getInitials = () => {
    if (!userInfo) return "U";
    const first = userInfo.firstname?.charAt(0) || "";
    const last = userInfo.lastname?.charAt(0) || "";
    return `${first}${last}`.toUpperCase() || "U";
  };

  const fullName = userInfo
    ? `${userInfo.firstname || ""} ${userInfo.lastname || ""}`
    : "User";

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(247, 248, 242, 0.8)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#2BAD65" }} />
            <Typography sx={{ mt: 2, color: "rgba(26, 26, 26, 0.5)" }}>
              Saving changes...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Settings sx={{ fontSize: 16 }} />
                  Settings
                </Typography>
              </Breadcrumbs>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Account Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(26, 26, 26, 0.4)",
                  mt: 0.5,
                }}
              >
                Manage your profile and account preferences
              </Typography>
            </Grid>

            <Grid item>
              <Chip
                icon={<School sx={{ fontSize: 16 }} />}
                label={`${userInfo?.firstname || "Student"}`}
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  border: "1px solid rgba(43, 173, 101, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Settings Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Grid container spacing={3}>
            {/* Profile Section */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(43, 173, 101, 0.06)",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: "#2BAD65",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "1.5rem",
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#1a1a1a" }}
                    >
                      {fullName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                    >
                      {userInfo?.email || ""}
                    </Typography>
                    <Chip
                      label="Verified"
                      size="small"
                      icon={<Verified sx={{ fontSize: 14 }} />}
                      sx={{
                        mt: 0.5,
                        backgroundColor: "rgba(160, 239, 72, 0.12)",
                        color: "#2BAD65",
                        fontWeight: 600,
                        fontSize: "0.6rem",
                      }}
                    />
                  </Box>
                </Box>

                <Divider
                  sx={{ mb: 3, backgroundColor: "rgba(43, 173, 101, 0.06)" }}
                />

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1a1a1a", mb: 2 }}
                >
                  Personal Information
                </Typography>

                <form onSubmit={handleSaveProfile}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="firstName"
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="lastName"
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="email"
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<Save />}
                        sx={{
                          backgroundColor: "#2BAD65",
                          color: "#ffffff",
                          fontWeight: 600,
                          px: 4,
                          py: 1.2,
                          borderRadius: 2,
                          "&:hover": {
                            backgroundColor: "#239A56",
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px rgba(43, 173, 101, 0.3)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Save Changes
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>

            {/* Right Column - Settings & Password */}
            <Grid item xs={12} md={5}>
              {/* Change Password */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  mb: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(43, 173, 101, 0.06)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1a1a1a", mb: 2 }}
                >
                  Change Password
                </Typography>

                <TextField
                  fullWidth
                  name="currentPassword"
                  label="Current Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.04)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleToggleConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.04)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.04)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleChangePassword}
                  sx={{
                    color: "#2BAD65",
                    borderColor: "rgba(43, 173, 101, 0.2)",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#2BAD65",
                      backgroundColor: "rgba(43, 173, 101, 0.04)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Update Password
                </Button>
              </Paper>

              {/* App Settings */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(43, 173, 101, 0.06)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1a1a1a", mb: 2 }}
                >
                  App Settings
                </Typography>

                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications}
                        onChange={() => handleSettingChange("notifications")}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#2BAD65",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#2BAD65",
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#1a1a1a" }}
                        >
                          Push Notifications
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                        >
                          Receive course updates and reminders
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailUpdates}
                        onChange={() => handleSettingChange("emailUpdates")}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#2BAD65",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#2BAD65",
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#1a1a1a" }}
                        >
                          Email Updates
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                        >
                          Get weekly course newsletters
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.twoFactorAuth}
                        onChange={() => handleSettingChange("twoFactorAuth")}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#2BAD65",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#2BAD65",
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#1a1a1a" }}
                        >
                          Two-Factor Authentication
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                        >
                          Extra security for your account
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Stack>

                <Divider
                  sx={{ my: 2, backgroundColor: "rgba(43, 173, 101, 0.06)" }}
                />

                <Button
                  variant="text"
                  fullWidth
                  startIcon={<Delete />}
                  sx={{
                    color: "#FF6B6B",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(255, 107, 107, 0.04)",
                    },
                  }}
                >
                  Delete Account
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            backgroundColor:
              snackbar.severity === "success" ? "#2BAD65" : "#FF6B6B",
            color: "#ffffff",
            "& .MuiAlert-icon": {
              color: "#ffffff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentSetting;
